import { logException } from "@wss/error-tracking";
import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import ct from "class-types.macro";
import { cls } from "cls";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { Store } from "@wss/store";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@clarkinc/zest-design-system/ui";
import NotificationCenter from "~/common/Notification/NotificationCenter";
import {
  CompatibleModel,
  MultiSelectTopper,
} from "~/common/Store/models/Category";
import PaginationModel from "~/common/Store/models/Pagination";
import {
  GroupMember,
  Product,
  PromotedProductBrandAd,
} from "~/common/Store/models/ProductListing";
import { RootState } from "~/common/Store/models/RootReadonly";
import { Topper } from "~/common/Store/models/Topper";
import { PreviouslyPurchasedItems, User } from "~/common/Store/models/User";
import CompareProducts from "./components/CompareProducts";
import PaginationComponent from "./Pagination/PaginationComponent";
import PromotedProductAd from "./ProductBox/PromotedProductAd";
import ProductBox from "./ProductBox/index";
import { PurchasingGroupProps } from "./PurchasingGroupPage/purchasingGroupPageResult";
import QuickFilterContainer from "./QuickFilter/Container";
import { FacetNavigation } from "./Sidebar/Filters";
import TopperV2, { TopperSection, DisplayType } from "./components/Topper";
import { CompareProductsState } from "./compareProductStore";
import { ChromeBarState } from "./Toolbar/useChromeBarComponentStore";
import {
  getResponseErrorMessage,
  shouldLogError,
} from "~/common/helpers/getProductPurchasingErrorHandling";

const LoginModalWrapper = React.lazy(
  () => import("~/common/LoginModal/LoginModalWrapper"),
);

import(
  /* webpackChunkName: "modals-standalone" */ "~/bootstrap/less/modals-standalone.less"
);

interface Props {
  items: Product[];
  filtersSelected: boolean;
  pagination: PaginationModel | undefined;
  topper?: Topper;
  pageUrl?: string;
  displayTopper?: boolean;
  hasCustomizableProductsOverride?: boolean;
  chromeBarStore: Store<ChromeBarState>;
  compareProductsStore?: Store<CompareProductsState>;
  purchasingGroupProps?: PurchasingGroupProps;
  belowListingComponent?: JSX.Element;
  user: User;
  mounted: boolean;
  discountCode?: string;
  showSaleStockNotice?: boolean;
  shouldPreselectVirtualGrouping?: boolean;
  facetsWithMultipleSelections?: string[];
  promotedProductBrandAd?: PromotedProductBrandAd;
  facetNavigation: FacetNavigation;
  ignoreCategoryFacets?: boolean;
  compatibleModels?: CompatibleModel[];
  multiSelectTopper?: MultiSelectTopper;
  setIsFilterRemoved?: (isCleared: boolean) => void;
  compareProductsToggleRef?: React.RefObject<HTMLButtonElement>;
}

type ShoppingCartItem = {
  outletItemId: number;
  isOutletItem: number;
  itemNumber: string;
};

interface AutoReorderItemsRequest {
  itemNumbers: string[];
}

export async function getPreviouslyPurchasedItems(): Promise<
  Throwable<PreviouslyPurchasedItems>
> {
  const endpoint = "/api/productdetails/getproductpurchasingactivity";
  try {
    const url = new URL(endpoint, window.location.origin);
    const result = await fetch(url.href);
    if (!result.ok) {
      const error = new Error(getResponseErrorMessage(result));

      return {
        error,
        isError: true,
      };
    }
    const previouslyPurchasedData: PreviouslyPurchasedItems =
      await result.json();
    return { isError: false, value: previouslyPurchasedData };
  } catch (error) {
    // Don't log -- resulted in extra noise in Sentry related to network
    // issues or server-side errors (which should be logged server-side already)
    return { isError: true, error: toError(error) };
  }
}

const ProductBoxes = ({
  items,
  filtersSelected,
  topper,
  displayTopper = false,
  pageUrl = "",
  pagination,
  hasCustomizableProductsOverride = false,
  chromeBarStore,
  compareProductsStore,
  purchasingGroupProps,
  belowListingComponent,
  user,
  mounted,
  discountCode,
  showSaleStockNotice = false,
  shouldPreselectVirtualGrouping = false,
  promotedProductBrandAd,
  facetsWithMultipleSelections,
  facetNavigation,
  ignoreCategoryFacets = false,
  compatibleModels,
  multiSelectTopper,
  setIsFilterRemoved,
  compareProductsToggleRef,
}: Props) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const { active: compareProductsActive, items: compareItems } =
    compareProductsStore?.useState((state) => state) || {
      active: false,
      items: [],
    };

  const compareProductsItemsRefs = React.useRef<HTMLButtonElement[]>([]);
  const removeComparisonItem = (itemNumber: string, index: number) => {
    const updatedItems = compareItems.filter(
      (item) => item.itemNumber !== itemNumber,
    );

    if (updatedItems.length === 0) {
      compareProductsToggleRef?.current?.focus();
    } else {
      const focusIndex = index === 0 ? 1 : index - 1;
      compareProductsItemsRefs.current[focusIndex]?.focus();
    }
    compareProductsStore?.setState({
      items: updatedItems,
    });
  };
  const clearComparisonItems = () =>
    compareProductsStore?.setState({ items: [] });

  const [currentUrl, setCurrentUrl] = useState(pageUrl);
  const activeProductListingView = chromeBarStore.useState(
    (state) => state.view,
  );
  const { totalPages = 1, currentPage = 1 } = pagination || {};
  const [itemsOnAutoReorder, setItemsOnAutoReorder] = useState<string[]>([]);
  const [cartItems, setCartItems] = useState<ShoppingCartItem[]>([]);
  const [previouslyPurchasedItems, setPreviouslyPurchasedItems] =
    useState<PreviouslyPurchasedItems>({});
  const [previouslyPurchasedItemsKeys, setPreviouslyPurchasedItemsKeys] =
    useState<string[]>([]);

  const [isQuantityDiscountPopoverOpen, setIsQuantityDiscountPopoverOpen] =
    useState("");

  const [isCompareErrorModalOpen, setIsCompareErrorModalOpen] = useState(false);

  useEffect(() => {
    setCurrentUrl(window.location.pathname + window.location.search);

    const populateItemsOnReorder = async () => {
      const endpointUrl = "/api/myaccount/autoreorderitems";
      const request: AutoReorderItemsRequest = {
        itemNumbers: [],
      };

      items.forEach((product) =>
        !product.grouping
          ? request.itemNumbers.push(product.itemNumber.toUpperCase())
          : product.groupingAttributes.groupMembers.forEach((groupMember) =>
              request.itemNumbers.push(groupMember.itemNumber.toUpperCase()),
            ),
      );

      try {
        const response = await fetch(endpointUrl, {
          method: "POST",
          headers: {
            "Content-type": "application/json",
          },
          body: JSON.stringify(request),
        });
        if (!response.ok) {
          throw new Error(
            `Failed to fetch auto reorder items, status ${response.status}`,
          );
        }

        const result = await response.json<string[]>();

        setItemsOnAutoReorder(result.map((i: string) => i.toLowerCase()));
      } catch {
        // Do nothing
      }
    };

    if (user.registered) {
      populateItemsOnReorder();
    }
  }, [items, user.registered]);

  useEffect(() => {
    const shouldGetShoppingCartItems = items.some(
      (x) => x.outletAttributes?.isOutlet,
    );

    if (shouldGetShoppingCartItems) {
      (async function getShoppingCartItems() {
        const endpointUrl = "/api/shoppingcart/items";

        try {
          const response = await fetch(`${endpointUrl}`);

          if (!response.ok) {
            throw new Error(
              `Failed to fetch shopping cart items, status ${response.status}`,
            );
          }

          const result = await response.json<ShoppingCartItem[]>();

          const camelcaseKeysImport = import("camelcase-keys");
          const { default: camelcaseKeys } = await camelcaseKeysImport;
          const cartItems = camelcaseKeys(result);
          setCartItems(cartItems);
        } catch {
          // Do nothing
        }
      })();
    }
  }, [items]);

  const isOutletItemAlreadyInCart = (product: Product) =>
    product.outletAttributes?.isOutlet &&
    cartItems.some(
      (x) => x.outletItemId === product.outletAttributes.outletProductId,
    );

  const tabletBreakPoint = 768;

  // Calculate desired row by looking at number of columns at various breakpoints
  // Included to maintain DOM order for accessibility
  const getBrandAdGridIndex = () => {
    const gridDesktopRow = 1;
    const gridMobileRow = 1;
    const desktopBreakPoint = 1100;
    const maxCols = 6;

    const columnsPerWidth = [
      {
        // Minimum number of cols, so width 0-499 will have 2 cols.
        width: 0,
        cols: 2,
      },
      {
        width: 500,
        cols: 3,
      },
      {
        width: desktopBreakPoint,
        cols: 4,
      },
      {
        width: 1430,
        cols: 5,
      },
      {
        width: 1650,
        cols: maxCols,
      },
    ];

    const currentBreakPoint = columnsPerWidth.filter(
      (c) => c.width <= window?.innerWidth,
    );

    const isServerSideRendering = window?.innerWidth === undefined;

    const insertRow =
      isServerSideRendering || window?.innerWidth >= desktopBreakPoint
        ? gridDesktopRow
        : gridMobileRow;

    const colsPerRow =
      !isServerSideRendering && !!currentBreakPoint
        ? currentBreakPoint[currentBreakPoint.length - 1]?.cols
        : maxCols;

    const insertIndex = colsPerRow
      ? insertRow * colsPerRow
      : insertRow * maxCols;

    return {
      brandAdGridIndex: insertIndex,
      numberOfCols: colsPerRow ?? maxCols,
    };
  };

  const [brandAdPlacementData, setBrandAdPlacementData] = useState(
    getBrandAdGridIndex(),
  );

  const handleResize = () => {
    // Don't adjust layout if video is fullscreen or it may exit fullscreen mode
    if (currentPage === 1 && !document.fullscreenElement) {
      setBrandAdPlacementData(getBrandAdGridIndex());
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize, false);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function initPreviouslyPurchasedItems() {
      async function handlePreviouslyPurchasedItems() {
        const result = await getPreviouslyPurchasedItems();
        if (result.isError) {
          return;
        }
        setPreviouslyPurchasedItems(result.value);
        setPreviouslyPurchasedItemsKeys(Object.keys(result.value));
      }

      if (user.registered) {
        handlePreviouslyPurchasedItems();
      }
    },
    [user.registered],
  );

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
  };

  const findPreviouslyPurchasedVGItemNumber = (groupMembers: GroupMember[]) => {
    const vgItemNumbers = groupMembers.map((groupMember) =>
      groupMember.itemNumber.toUpperCase(),
    );

    const previouslyPurchasedVGItemNumbers = vgItemNumbers.filter(
      (item: string) => previouslyPurchasedItemsKeys.includes(item),
    );
    const dates = Object.entries(previouslyPurchasedItems)
      .filter(([key]) => previouslyPurchasedVGItemNumbers.includes(key))
      .map(([, value]) => new Date(value));

    return dates.length > 0
      ? new Date(
          Math.max(...dates.map((date) => date.getTime())),
        ).toLocaleDateString("en-US", dateOptions)
      : undefined;
  };

  const findPreviouslyPurchasedItemNumber = (itemNumber: string) => {
    const purchasedDate = previouslyPurchasedItems[itemNumber];
    if (purchasedDate !== undefined) {
      return new Date(purchasedDate).toLocaleDateString("en-US", dateOptions);
    }
    return undefined;
  };

  const handleCompareErrorModalOpen = useCallback(
    () => setIsCompareErrorModalOpen(true),
    [],
  );

  const handleCompareErrorModalClose = useCallback(
    () => setIsCompareErrorModalOpen(false),
    [],
  );

  const getProductBoxes = () => {
    const productBoxes = items.map((product, index) => (
      <ProductBox
        key={product.itemNumber}
        product={product}
        mounted={mounted}
        user={user}
        lazyLoadImages={index > 12}
        index={index}
        compareProductsStore={compareProductsStore}
        productListingView={activeProductListingView}
        hasCustomizableProductsOverride={hasCustomizableProductsOverride}
        purchasingGroupProps={purchasingGroupProps}
        itemsOnAutoReorder={itemsOnAutoReorder}
        isItemAlreadyInCart={isOutletItemAlreadyInCart(product)}
        discountCode={discountCode}
        showSaleStockNotice={showSaleStockNotice}
        shouldPreselectVirtualGrouping={shouldPreselectVirtualGrouping}
        facetsWithMultipleSelections={facetsWithMultipleSelections}
        previouslyPurchased={
          product.groupingAttributes.isVirtual
            ? findPreviouslyPurchasedVGItemNumber(
                product.groupingAttributes.groupMembers,
              )
            : findPreviouslyPurchasedItemNumber(
                product.itemNumber.toUpperCase(),
              )
        }
        setIsQuantityDiscountPopoverOpen={setIsQuantityDiscountPopoverOpen}
        isQuantityDiscountPopoverOpen={isQuantityDiscountPopoverOpen}
        handleCompareErrorModalOpen={handleCompareErrorModalOpen}
      />
    ));

    if (promotedProductBrandAd) {
      const indexToInsertAd =
        activeProductListingView === "list"
          ? 1
          : brandAdPlacementData.brandAdGridIndex;

      productBoxes.splice(
        Math.min(indexToInsertAd, items.length),
        0,
        <PromotedProductAd
          key="brandAd"
          user={user}
          brandAd={promotedProductBrandAd}
          productListingView={activeProductListingView}
          mounted={mounted}
          fillPartialRowForGridView={
            brandAdPlacementData.brandAdGridIndex >= items.length &&
            items.length % brandAdPlacementData.numberOfCols > 0
          }
          isTabletView={window?.innerWidth < tabletBreakPoint}
          itemsLength={items.length}
          gridRef={gridRef}
        />,
      );
    }

    return productBoxes;
  };

  const listingHasResults = items.length > 0 || !!promotedProductBrandAd;

  return (
    <div id="main" className="skip-to-main-target">
      <NotificationCenter />
      {filtersSelected && (
        <QuickFilterContainer
          facetNavigation={facetNavigation}
          compatibleModels={compatibleModels}
          isLeafCategoryPage={ignoreCategoryFacets}
          currentUrl={currentUrl}
          setIsFilterRemoved={setIsFilterRemoved}
        />
      )}
      {compareProductsActive && (
        <CompareProducts
          className={filtersSelected ? "border-t-0" : ""}
          items={compareItems}
          onRemove={removeComparisonItem}
          onClear={clearComparisonItems}
          itemRefs={compareProductsItemsRefs}
        />
      )}
      {!compareProductsActive &&
        displayTopper &&
        topper &&
        topper.isActive &&
        pageUrl &&
        !multiSelectTopper &&
        !!topper.sections.length && (
          <TopperV2.Standard
            sections={topper.sections.map(
              (section) =>
                ({
                  sectionId: section.id,
                  name: section.name,
                  displayType: section.displayType,
                  items: section.items.map((item) => ({
                    itemId: item.id,
                    name: item.name,
                    image: item.image,
                    url: item.url,
                    description: item.description,
                  })),
                }) as TopperSection,
            )}
          />
        )}
      {!compareProductsActive &&
      multiSelectTopper &&
      multiSelectTopper.currentSection.items.length > 0 ? (
        <TopperV2.MultiSelect
          sections={[
            {
              displayType: DisplayType.ImageAndTitle,
              sectionId: 1,
              ...multiSelectTopper.currentSection,
            } as TopperSection,
          ]}
          previousLink={multiSelectTopper.previousLink}
          previousSectionName={multiSelectTopper.previousSectionName}
        />
      ) : null}

      {listingHasResults ? (
        <>
          <div
            /* TODO: Once pricing and ATC styles are completely converted to tailwind update styles here.
             * The reason I didn't touch the styles is because pricing and ATC depend on it. */
            ref={gridRef}
            id="product_listing"
            data-testid="product-listing-container"
            className={`ag ag-${activeProductListingView} overflow-visible
             ${
               (purchasingGroupProps?.isPurchasingGroup && totalPages > 1) ||
               totalPages === 1
                 ? ct("mb-5")
                 : ""
             } ${
               activeProductListingView === "grid"
                 ? cls(
                     "grid",
                     "grid-cols-2",
                     "xsl:grid-cols-3",
                     "lz:grid-cols-4",
                     "xxl:grid-cols-5",
                     "xxxl:grid-cols-6",
                     "border-0",
                     "border-solid",
                     "border-l",
                     "border-gray-200",
                     "mt-0",
                   )
                 : cls(
                     "border-x",
                     "border-t-0",
                     "border-b",
                     "border-solid",
                     "border-gray-200",
                     "pl-0",
                     "-mt-px",
                   )
             }`}
          >
            {getProductBoxes()}
          </div>
          {belowListingComponent}
        </>
      ) : (
        <h1>Sorry! No results found</h1>
      )}

      {pagination && totalPages > 1 && (
        <div
          data-testid="paging"
          id="paging"
          className={ct("clear-both", "p-3", "text-center")}
        >
          <PaginationComponent paging={pagination} currentUrl={currentUrl} />
        </div>
      )}

      {mounted && (
        <>
          {ReactDOM.createPortal(
            <Suspense fallback={null}>
              <LoginModalWrapper />
            </Suspense>,

            document.querySelector("body") as Element,
          )}
        </>
      )}

      <Modal
        open={isCompareErrorModalOpen}
        onOpenChange={setIsCompareErrorModalOpen}
        id="compare-products-error-modal"
        data-testid="compare-products-error-modal"
        theme="wss"
        className="min-w-[528px] hidden md:block"
      >
        <ModalHeader className="flex p-4 items-center">
          You can compare up to 4 items at a time.
        </ModalHeader>
        <ModalBody>
          Remove 1 or more items before adding another item to compare.
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            color="green"
            onClick={() => handleCompareErrorModalClose()}
          >
            Ok
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export const mapStateToProps = (state: RootState, ownProps: Props) => {
  if (!ownProps.mounted) return { user: ownProps.user };

  return { user: state.user };
};

export default connect(mapStateToProps)(ProductBoxes);
